import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  AppBar,
  Toolbar,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SecurityIcon from '@mui/icons-material/Security';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import BoltIcon from '@mui/icons-material/Bolt';
import { useAuth } from '../contexts/AuthContext';

const features = [
  {
    icon: <SpeedIcon sx={{ fontSize: 40 }} />,
    title: 'Blitzschnelle Generierung',
    description: 'Erstellen Sie komplexe technische Dokumentation in Sekunden statt Stunden.',
  },
  {
    icon: <AutoAwesomeIcon sx={{ fontSize: 40 }} />,
    title: 'KI-gestützte Qualität',
    description: 'Modernste LLM-Technologie für präzise und konsistente Inhalte.',
  },
  {
    icon: <SecurityIcon sx={{ fontSize: 40 }} />,
    title: 'Industrie-Standards',
    description: 'DITA, S1000D, OPC UA und weitere Formate out-of-the-box.',
  },
  {
    icon: <IntegrationInstructionsIcon sx={{ fontSize: 40 }} />,
    title: 'Nahtlose Integration',
    description: 'API-first Design für einfache Integration in bestehende Systeme.',
  },
];

const testimonials = [
  {
    quote: 'Mit der DZ Content Factory haben wir die Dokumentationszeit für 500 Sensoren von 3 Monaten auf 2 Tage reduziert. Der ROI war sofort sichtbar.',
    author: 'Thomas Müller',
    role: 'Head of Digital Twin, Siemens AG',
    avatar: 'TM',
  },
  {
    quote: 'Vollautomatische ISO-konforme Dokumentation – das hat uns Compliance-Audits erheblich vereinfacht.',
    author: 'Dr. Sarah Weber',
    role: 'CTO, Bosch IoT Solutions',
    avatar: 'SW',
  },
  {
    quote: 'Von der Pilotphase bis zum Enterprise-Rollout nahtlos skaliert. €150.000 an manuellen Kosten gespart.',
    author: 'Michael Schmidt',
    role: 'VP Operations, BASF Digital',
    avatar: 'MS',
  },
];

const pricingPlans = [
  {
    name: 'Pilot Light',
    price: '7.500',
    period: ' einmalig',
    description: 'Flexibler Einstieg ohne Monatszwang',
    features: [
      'Bis zu 50 Sensoren (1 Anlage/Standort)',
      'Daten- & Dokumentationsgenerierung (MD, HTML, PDF)',
      'Exporte: JSON, XML, CSV',
      '2h Remote-Schulung',
      '14 Tage Support',
      'Upgrade auf Pilot jederzeit möglich',
    ],
    cta: 'Pilot Light anfragen',
    ctaTo: '/contact',
    popular: true,
    icon: 'bolt',
    gradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.15), rgba(0, 180, 216, 0.1))',
    borderColor: '#14b8a6',
  },
  {
    name: 'Credits',
    price: 'ab 2.400',
    period: '',
    description: 'Pay-per-Use für Updates & Audits',
    features: [
      '1 Credit = 1 Generation Run (Daten + Doku + Exporte)',
      'Runs pro Anlage/Standort (klarer Scope)',
      'Sensor-Buckets: S (≤50), M (≤100), L (≤250), XL (≤500)',
      'Audit Add-on pro Run verfügbar',
      'Kein Abo – kaufen wenn benötigt',
      'Mehrere Standorte = mehrere Scopes',
    ],
    cta: 'Credits anfragen',
    ctaTo: '/contact',
    popular: false,
    icon: 'offer',
    gradient: 'linear-gradient(135deg, rgba(0, 180, 216, 0.1), transparent)',
    borderColor: 'rgba(0, 180, 216, 0.3)',
  },
  {
    name: 'Pilot',
    price: '15.000',
    period: ' einmalig',
    description: 'Für Proof-of-Concept und größere Pilotprojekte',
    features: [
      'Bis zu 100 Sensoren',
      'Initiale Dokumentation',
      'Alle Exportformate (JSON, XML, CSV)',
      'Integration in bestehende Systeme',
      '30 Tage Support',
      'Schulung für Ihr Team',
    ],
    cta: 'Pilot anfragen',
    ctaTo: '/contact',
    popular: false,
    icon: 'rocket',
    gradient: 'linear-gradient(135deg, rgba(0, 180, 216, 0.06), transparent)',
    borderColor: 'rgba(0, 180, 216, 0.25)',
  },
  {
    name: 'Professional',
    price: '5.000',
    period: '/Monat',
    description: 'Für kontinuierliche Dokumentation und Echtzeit-Updates',
    features: [
      'Bis zu 500 Sensoren',
      'Automatische Updates bei Änderungen',
      'Echtzeit-Synchronisation',
      'OPC UA & MQTT Integration',
      'Mehrsprachige Dokumentation',
      'Priority Support (8 Std. Reaktion)',
      'Monatliche Reports',
    ],
    cta: 'Professional wählen',
    ctaTo: '/register',
    popular: false,
    icon: 'star',
    gradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.12), rgba(0, 180, 216, 0.06))',
    borderColor: 'rgba(20, 184, 166, 0.6)',
  },
  {
    name: 'Enterprise',
    price: '15.000',
    period: '/Monat',
    description: 'Unbegrenzte Skalierung für Großunternehmen',
    features: [
      'Unbegrenzte Sensoren & Standorte',
      'Alle Professional Features',
      'TwinDataMaker Integration',
      'On-Premise / Private Cloud Option',
      'Dedizierter Account Manager',
      'Custom Integrationen & APIs',
      'SLA 99.9% Uptime Garantie',
      '24/7 Premium Support',
    ],
    cta: 'Enterprise anfragen',
    ctaTo: '/contact',
    popular: false,
    icon: 'business',
    gradient: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1), transparent)',
    borderColor: 'rgba(168, 85, 247, 0.3)',
  },
];

export function LandingPage() {
  const { isAuthenticated } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a1929' }}>
      {/* Navigation */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          background: 'rgba(10, 25, 41, 0.8)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid rgba(0, 180, 216, 0.1)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src="/logo.png"
                alt="DZ Content Factory"
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  objectFit: 'cover',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.25)'
                }}
              />
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                DZ Content Factory
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Button
                component={Link}
                to="/impressum"
                sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Impressum
              </Button>
              <Button
                component={Link}
                to="/privacy"
                sx={{ color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
              >
                Datenschutz
              </Button>
              {isAuthenticated ? (
                <Button
                  component={Link}
                  to="/app"
                  variant="contained"
                  sx={{
                    background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                  }}
                >
                  Zur App
                </Button>
              ) : (
                <>
                  <Button
                    component={Link}
                    to="/login"
                    sx={{ color: '#e2e8f0' }}
                  >
                    Anmelden
                  </Button>
                  <Button
                    component={Link}
                    to="/register"
                    variant="contained"
                    sx={{
                      background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                    }}
                  >
                    Registrieren
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Hero Section */}
      <Box
        className="industrial-bg"
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 10, md: 16 },
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Gradient overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 50% 0%, rgba(0, 180, 216, 0.15), transparent 50%)',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="🏭 Dokumentation as a Service für Industrie 4.0"
            sx={{
              mb: 4,
              px: 2,
              py: 2.5,
              background: 'rgba(0, 180, 216, 0.15)',
              border: '1px solid rgba(0, 180, 216, 0.3)',
              fontSize: '0.9rem',
            }}
          />
          
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 800,
              color: '#e2e8f0',
              lineHeight: 1.2,
              mb: 3,
            }}
          >
            Digitale Zwillinge.{' '}
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(90deg, #00b4d8, #14b8a6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Automatisch dokumentiert.
            </Box>
          </Typography>
          
          <Typography
            variant="h5"
            color="text.secondary"
            sx={{ mb: 5, maxWidth: 700, mx: 'auto', lineHeight: 1.6 }}
          >
            KI-gestützte Dokumentation für Industrie 4.0. Von 1.000 Sensoren zu vollständiger 
            ISO-konformer Dokumentation in 24 Stunden statt 6 Monaten.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              component={Link}
              to={isAuthenticated ? '/app' : '#pricing'}
              variant="contained"
              size="large"
              startIcon={<RocketLaunchIcon />}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
                '&:hover': {
                  boxShadow: '0 12px 40px rgba(0, 180, 216, 0.4)',
                },
              }}
              onClick={() => !isAuthenticated && document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              {isAuthenticated ? 'Zur App' : 'Pilot starten'}
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                borderColor: 'rgba(0, 180, 216, 0.5)',
                color: '#e2e8f0',
                '&:hover': {
                  borderColor: '#00b4d8',
                  background: 'rgba(0, 180, 216, 0.1)',
                },
              }}
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Mehr erfahren
            </Button>
          </Box>

          {isAuthenticated && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              ✨ Demo-Zugang aktiv – testen Sie alle Funktionen
            </Typography>
          )}
        </Container>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 10, md: 16 }, bgcolor: 'rgba(16, 42, 67, 0.5)' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#e2e8f0',
              mb: 2,
            }}
          >
            Warum DZ Content Factory?
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Modernste KI-Technologie trifft auf Industrie-Expertise
          </Typography>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
                    border: '1px solid rgba(0, 180, 216, 0.15)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      border: '1px solid rgba(0, 180, 216, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{ color: '#00b4d8', mb: 2 }}>
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#e2e8f0' }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Social Proof Section */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#e2e8f0',
              mb: 2,
            }}
          >
            Vertraut von Industrie-Experten
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Führende Unternehmen nutzen DZ Content Factory
          </Typography>

          <Grid container spacing={4}>
            {testimonials.map((testimonial, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
                    border: '1px solid rgba(0, 180, 216, 0.15)',
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                      {[...Array(5)].map((_, i) => (
                        <StarIcon key={i} sx={{ color: '#f97316', fontSize: 20 }} />
                      ))}
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{ color: '#e2e8f0', mb: 3, fontStyle: 'italic', lineHeight: 1.7 }}
                    >
                      "{testimonial.quote}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          color: 'white',
                        }}
                      >
                        {testimonial.avatar}
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e2e8f0' }}>
                          {testimonial.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {testimonial.role}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ROI Section */}
      <Box sx={{ py: { xs: 10, md: 16 }, bgcolor: '#0a1929' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#e2e8f0',
              mb: 2,
            }}
          >
            Ihr Return on Investment
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 8, maxWidth: 700, mx: 'auto' }}
          >
            Manuelle Dokumentation kostet €50–€200 pro Stunde. Bei 1.000 Sensoren sparen Sie 
            6 Monate Arbeit – wir machen das in 24 Stunden.
          </Typography>
          
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                textAlign: 'center', 
                p: 4,
                background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
                border: '1px solid rgba(0, 180, 216, 0.2)',
              }}>
                <Typography variant="h2" sx={{ color: '#00b4d8', fontWeight: 800 }}>
                  €150k+
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Durchschnittliche Kosteneinsparung pro Jahr
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                textAlign: 'center', 
                p: 4,
                background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
                border: '1px solid rgba(20, 184, 166, 0.2)',
              }}>
                <Typography variant="h2" sx={{ color: '#14b8a6', fontWeight: 800 }}>
                  99%
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  Weniger manuelle Dokumentationsarbeit
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ 
                textAlign: 'center', 
                p: 4,
                background: 'linear-gradient(135deg, rgba(16, 42, 67, 0.8), rgba(36, 59, 83, 0.8))',
                border: '1px solid rgba(168, 85, 247, 0.2)',
              }}>
                <Typography variant="h2" sx={{ color: '#a855f7', fontWeight: 800 }}>
                  100%
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  ISO & Industrie 4.0 Compliance
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: { xs: 10, md: 16 }, bgcolor: 'rgba(16, 42, 67, 0.5)' }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              color: '#e2e8f0',
              mb: 2,
            }}
          >
            Preise & Pakete
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ textAlign: 'center', mb: 8, maxWidth: 600, mx: 'auto' }}
          >
            Pilot Light + Credits: Dokumentation & Daten ohne Monatszwang – mit optionalen Enterprise-Features
          </Typography>

          <Grid container spacing={4} justifyContent="center">
            {pricingPlans
              .filter((plan) => ['Pilot Light', 'Credits', 'Enterprise'].includes(plan.name))
              .map((plan, index) => {
              const isSelected = selectedPlan === plan.name;

              return (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  onClick={() => setSelectedPlan(plan.name)}
                  sx={{
                    height: '100%',
                    background: plan.gradient,
                    border: isSelected ? '2px solid #e2e8f0' : (plan.popular ? `2px solid ${plan.borderColor}` : `1px solid ${plan.borderColor}`),
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: isSelected ? '0 20px 60px rgba(226, 232, 240, 0.12)' : undefined,
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: isSelected
                        ? '0 24px 70px rgba(226, 232, 240, 0.16)'
                        : (plan.popular 
                          ? '0 20px 60px rgba(20, 184, 166, 0.3)' 
                          : '0 20px 40px rgba(0, 0, 0, 0.3)'),
                    },
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="Beliebteste Wahl"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'linear-gradient(135deg, #14b8a6, #00b4d8)',
                        fontWeight: 600,
                      }}
                    />
                  )}
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                      <Box sx={{ color: plan.popular ? '#14b8a6' : '#00b4d8', mb: 2 }}>
                        {plan.icon === 'rocket' && <RocketLaunchIcon sx={{ fontSize: 48 }} />}
                        {plan.icon === 'star' && <StarIcon sx={{ fontSize: 48 }} />}
                        {plan.icon === 'business' && <BusinessIcon sx={{ fontSize: 48 }} />}
                        {plan.icon === 'offer' && <LocalOfferIcon sx={{ fontSize: 48 }} />}
                        {plan.icon === 'bolt' && <BoltIcon sx={{ fontSize: 48 }} />}
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 1 }}>
                        {plan.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {plan.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                        {plan.price !== 'Individuell' && (
                          <Typography variant="h6" color="text.secondary">€</Typography>
                        )}
                        <Typography
                          variant="h2"
                          sx={{ fontWeight: 800, color: '#e2e8f0', mx: 0.5 }}
                        >
                          {plan.price}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {plan.period}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 4 }}>
                      {plan.features.map((feature, i) => (
                        <Box
                          key={i}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            py: 1,
                          }}
                        >
                          <CheckCircleIcon sx={{ color: '#14b8a6', fontSize: 20 }} />
                          <Typography variant="body2" color="text.secondary">
                            {feature}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Button
                      component={Link}
                      to={plan.ctaTo}
                      variant={plan.popular ? 'contained' : 'outlined'}
                      fullWidth
                      size="large"
                      onClick={(e) => e.stopPropagation()}
                      sx={
                        plan.popular
                          ? {
                              background: 'linear-gradient(135deg, #14b8a6, #00b4d8)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #2dd4bf, #00d9ff)',
                              },
                            }
                          : {
                              borderColor: plan.borderColor,
                              color: '#e2e8f0',
                              '&:hover': {
                                borderColor: '#00b4d8',
                                background: 'rgba(0, 180, 216, 0.1)',
                              },
                            }
                      }
                    >
                      {plan.cta}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              );
            })}
          </Grid>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Weitere Optionen
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                component={Link}
                to="/contact"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(0, 180, 216, 0.35)',
                  color: '#e2e8f0',
                  '&:hover': { borderColor: '#00b4d8', background: 'rgba(0, 180, 216, 0.08)' },
                }}
              >
                Pilot (15.000 € einmalig)
              </Button>
              <Button
                component={Link}
                to="/register"
                variant="outlined"
                sx={{
                  borderColor: 'rgba(20, 184, 166, 0.35)',
                  color: '#e2e8f0',
                  '&:hover': { borderColor: '#14b8a6', background: 'rgba(20, 184, 166, 0.08)' },
                }}
              >
                Professional (5.000 €/Monat)
              </Button>
            </Box>
          </Box>

          {selectedPlan === 'Credits' && (
            <Box sx={{ mt: 6, maxWidth: 900, mx: 'auto' }}>
              <Card sx={{ background: 'rgba(10, 25, 41, 0.6)', border: '1px solid rgba(0, 180, 216, 0.15)' }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 2, textAlign: 'center' }}>
                    Credit Packs – Preisdetails
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                    Credits gelten als Runs pro Anlage/Standort (Scope). Der Bucket richtet sich nach der Sensoranzahl im Scope.
                  </Typography>

                  <TableContainer>
                    <Table size="small" aria-label="Credit Packs">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }}>Bucket</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }}>Grenze</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }}>Pack</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }} align="right">Preis</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }} align="right">Runs</TableCell>
                          <TableCell sx={{ color: '#e2e8f0', fontWeight: 700 }} align="right">€/Run</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>S</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 50 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>S3</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">2.400 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">3</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">800 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>S</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 50 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>S6</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">4.200 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">6</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">700 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>M</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 100 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>M3</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">4.200 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">3</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">1.400 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>M</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 100 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>M6</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">7.200 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">6</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">1.200 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>L</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 250 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>L3</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">9.000 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">3</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">3.000 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>L</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 250 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>L6</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">15.000 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">6</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">2.500 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>XL</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 500 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>XL3</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">18.000 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">3</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">6.000 €</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell sx={{ color: '#e2e8f0' }}>XL</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>≤ 500 Sensoren</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>XL6</TableCell>
                          <TableCell sx={{ color: '#e2e8f0' }} align="right">30.000 €</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">6</TableCell>
                          <TableCell sx={{ color: 'text.secondary' }} align="right">5.000 €</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 1 }}>
                      Audit Add-on (pro Run)
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      S/M: +600 € · L: +1.500 € · XL: +3.000 €
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          )}
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 10, md: 16 } }}>
        <Container maxWidth="md">
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(0, 180, 216, 0.2), rgba(20, 184, 166, 0.2))',
              border: '1px solid rgba(0, 180, 216, 0.3)',
              textAlign: 'center',
              p: { xs: 4, md: 8 },
            }}
          >
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#e2e8f0', mb: 2 }}>
              Bereit für den Start?
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
              Testen Sie DZ Content Factory kostenlos mit 3 Anfragen. Keine Kreditkarte erforderlich.
            </Typography>
            <Button
              component={Link}
              to="/register"
              variant="contained"
              size="large"
              startIcon={<RocketLaunchIcon />}
              sx={{
                px: 5,
                py: 1.5,
                fontSize: '1.1rem',
                background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                boxShadow: '0 8px 32px rgba(0, 180, 216, 0.3)',
              }}
            >
              Jetzt kostenlos starten
            </Button>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 6,
          borderTop: '1px solid rgba(0, 180, 216, 0.1)',
          bgcolor: 'rgba(10, 25, 41, 0.8)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1,
                    background: 'linear-gradient(135deg, #00b4d8, #14b8a6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <TimelineIcon sx={{ color: 'white', fontSize: 22 }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#e2e8f0' }}>
                  DZ Content Factory
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 280 }}>
                Automatisierte Content-Erstellung für Digitale Zwillinge. Powered by AI.
              </Typography>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e2e8f0', mb: 2 }}>
                Produkt
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/app" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  App
                </Link>
                <Link to="/#pricing" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Preise
                </Link>
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e2e8f0', mb: 2 }}>
                Rechtliches
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link to="/impressum" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Impressum
                </Link>
                <Link to="/privacy" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.875rem' }}>
                  Datenschutz
                </Link>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e2e8f0', mb: 2 }}>
                Kontakt
              </Typography>
              <Typography variant="body2" color="text.secondary">
                DiKItal Solutions<br />
                Am Sportplatz 22<br />
                07552 Gera, Deutschland<br />
                info@dikitalsolutions.com
              </Typography>
            </Grid>
          </Grid>
          <Box sx={{ mt: 6, pt: 4, borderTop: '1px solid rgba(0, 180, 216, 0.1)', textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              © {new Date().getFullYear()} DiKItal Solutions. Alle Rechte vorbehalten.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
